import {
  createBigDecimal,
  divide,
  greaterThan,
  minus,
  plus,
  times,
} from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { AssetBorrow } from '../../types/assetBorrow'
import type { Transaction } from '../../types/transaction'
import { createAssetKey } from '../../utils'
import type {
  CreateCategoryTransactionParameters,
  CreateCategoryTransactionReturnTypes,
} from './types'

type CreateRepaymentTransactionParameters =
  CreateCategoryTransactionParameters & {
    readonly transfer: Transfer
  }

type CreateRepaymentTransactionReturnTypes =
  CreateCategoryTransactionReturnTypes

export const createRepaymentTransaction = ({
  actionId,
  timestamp,
  transfer,
  assetBalances,
  assetBorrows,
  usedBalances,
}: CreateRepaymentTransactionParameters): CreateRepaymentTransactionReturnTypes => {
  const key = createAssetKey(transfer.asset)

  // get asset borrow
  let remain = transfer.amount
  let assetBorrow: AssetBorrow = {
    amount: createBigDecimal(0n),
    value: createBigDecimal(0n),
    borrowedAmount: createBigDecimal(0n),
    borrowedValue: createBigDecimal(0n),
  }
  const targetAssetBorrows = assetBorrows.get(key) ?? []
  const newAssetBorrows: AssetBorrow[] = []
  for (const borrow of targetAssetBorrows) {
    // remain = 0 の場合
    // - newAssetBorrows に borrow data 追加
    if (remain.value === 0n) {
      newAssetBorrows.push(borrow)
      continue
    }

    // borrow.amount <= remain の場合
    // - remain -= borrow.amount
    // borrowedAmount > amount の場合は borrowed data は比率分追加して、余りを newAssetBorrows に追加
    // - assetBorrow に borrow data 追加
    if (!greaterThan(borrow.amount, remain)) {
      remain = minus(remain, borrow.amount)
      if (greaterThan(borrow.borrowedAmount, borrow.amount)) {
        const borrowedValuePerAmount = divide(
          times(borrow.borrowedValue, borrow.amount, 6),
          borrow.borrowedAmount,
          6,
        )
        assetBorrow = {
          amount: plus(assetBorrow.amount, borrow.amount),
          value: plus(assetBorrow.value, borrow.value),
          borrowedAmount: plus(assetBorrow.borrowedAmount, borrow.amount),
          borrowedValue: plus(
            assetBorrow.borrowedValue,
            borrowedValuePerAmount,
          ),
        }
        newAssetBorrows.push({
          amount: createBigDecimal(0n),
          value: createBigDecimal(0n),
          borrowedAmount: minus(borrow.borrowedAmount, borrow.amount),
          borrowedValue: minus(borrow.borrowedValue, borrowedValuePerAmount),
        })
        continue
      }
      assetBorrow = {
        amount: plus(assetBorrow.amount, borrow.amount),
        value: plus(assetBorrow.value, borrow.value),
        borrowedAmount: plus(assetBorrow.borrowedAmount, borrow.borrowedAmount),
        borrowedValue: plus(assetBorrow.borrowedValue, borrow.borrowedValue),
      }
      continue
    }

    // borrow.amount > remain の場合
    // - remain = 0
    // - assetBorrow に remain 分の borrow data 追加
    // - borrow から remain 分の borrow data を引いた残りを newAssetBorrows に追加
    const valuePerRemain = divide(
      times(borrow.value, remain, 6),
      borrow.amount,
      6,
    )
    const borrowedValuePerRemain = divide(
      times(borrow.borrowedValue, remain, 6),
      borrow.borrowedAmount,
      6,
    )
    assetBorrow = {
      amount: plus(assetBorrow.amount, remain),
      value: plus(assetBorrow.value, valuePerRemain),
      borrowedAmount: plus(assetBorrow.borrowedAmount, remain),
      borrowedValue: plus(assetBorrow.borrowedValue, borrowedValuePerRemain),
    }
    newAssetBorrows.push({
      amount: minus(borrow.amount, remain),
      value: minus(borrow.value, valuePerRemain),
      borrowedAmount: minus(borrow.borrowedAmount, remain),
      borrowedValue: minus(borrow.borrowedValue, borrowedValuePerRemain),
    })
    remain = createBigDecimal(0n)
  }
  if (remain.value > 0n) {
    throw new Error('Not enough borrowed amount')
  }
  if (newAssetBorrows.length > 0) {
    assetBorrows.set(key, newAssetBorrows)
  } else {
    assetBorrows.delete(key)
  }

  const balance = (assetBalances.get(key) ?? []).reduce(
    (acc, assetBalance) => plus(acc, assetBalance.amount),
    createBigDecimal(0n),
  )
  const borrowing = (assetBorrows.get(key) ?? []).reduce(
    (acc, assetBorrow) => plus(acc, assetBorrow.amount),
    createBigDecimal(0n),
  )

  const pl = minus(assetBorrow.borrowedValue, assetBorrow.value)
  const transaction: Transaction = {
    actionId,
    timestamp,
    order: 0,
    category: 'repayment',
    ins: [],
    outs: [
      {
        amount: transfer.amount,
        asset: transfer.asset,
        price: divide(assetBorrow.value, transfer.amount, 6),
        zeroCompleted: false,
        cost: assetBorrow.borrowedValue,
        balance,
        borrowing,
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: pl,
        totalPl: pl,
      },
    ],
  }

  return {
    transaction,
    assetBalances,
    assetBorrows,
    usedBalances,
  }
}
